/**
 * Inline run details — opens under its row in the Ledger grid (redesign
 * 2026-10-05; replaces the old under-table drawer). Light: thin dividers,
 * small grey labels, every text element has its own colour.
 */
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import ShimmerImage from '../../../components/ShimmerImage';
import type { LedgerLine, LedgerRunDetail } from '../../../services/aiControl/ledger';
import {
  attemptLine, mathLines, money, locationLabel, rubyValueTokens,
} from './ledgerModel';
import { tokensLabel } from '../rubyAi/units';
import { ErrorBanner } from '../rubyAi/ui';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[11px] text-stone-500">{label}</dt>
    <dd className="text-[12.5px] text-stone-900 break-words">{children}</dd>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold mb-1.5">{children}</h4>
);

function durationText(ms: number | undefined): string {
  if (!ms && ms !== 0) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

const CopyRunId: React.FC<{ runId: string }> = ({ runId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={`Copy run id ${runId}`}
      onClick={() => {
        void navigator.clipboard?.writeText(runId).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }, () => undefined);
      }}
      className="inline-flex items-center gap-1.5 min-h-[44px] -my-3 font-mono text-[12px] text-stone-900 hover:text-[#9F1239]"
    >
      {runId}
      {copied ? <Check size={13} className="text-emerald-700" /> : <Copy size={13} className="text-stone-400" />}
      <span className="sr-only">{copied ? 'Copied' : ''}</span>
    </button>
  );
};

const LedgerRunDetails: React.FC<{
  line: LedgerLine;
  detail: LedgerRunDetail | null;
  loading: boolean;
  error: string | null;
}> = ({ line, detail, loading, error }) => {
  const run = detail?.run;
  const charged = Math.round(line.tokensCharged ?? 0);
  const refunded = Math.round(line.tokensRefunded ?? 0);
  const reserved = Math.round(run?.tokensReserved ?? 0);
  const released = Math.max(0, reserved - charged - refunded);
  const feedback = run?.feedback ?? line.feedback;
  const math = run ? mathLines(run) : [];

  return (
    <div className="px-3 md:px-4 py-4 bg-stone-50/70 border-t border-stone-100 text-stone-900" data-testid="ledger-run-details">
      {line.free && (
        <p role="note" className="mb-3 text-[12px] text-emerald-900">
          <span className="font-semibold">Free AI tenant</span> — not charged; the vendor cost is our cost.
        </p>
      )}

      <dl className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-5 gap-y-3">
        <Field label="Where">{line.source?.surface || '—'}{line.source?.path && <span className="block font-mono text-[11px] text-stone-500">{line.source.path}</span>}</Field>
        <Field label="Location">{locationLabel(line) || '—'}</Field>
        <Field label="Vendor usage">{line.vendorSpent || '—'}</Field>
        <Field label="We paid">{money(line.vendorCostUsd ?? 0)}</Field>
        <Field label="× multiplier">×{line.multiplier} <span className="text-stone-500">({line.multiplierSource})</span></Field>
        <Field label="Ruby value">{tokensLabel(rubyValueTokens(line))}</Field>
        <Field label="Charged">{line.free ? 'Free AI' : `${tokensLabel(charged)} · ${money(line.chargedUsd ?? 0)}`}</Field>
        <Field label="Refunded · held · released">
          {tokensLabel(refunded)} · {run ? `${tokensLabel(reserved)} · ${tokensLabel(released)}` : '…'}
        </Field>
        <Field label="Model · version">{line.providerId ? `${line.providerId} · ` : ''}{line.modelId ?? '—'}{line.modelVersion ? ` v${line.modelVersion}` : ''}</Field>
        <Field label="Background">{line.keepBackground ? 'Kept (background step skipped)' : 'Replaced / n.a.'}</Field>
        <Field label="Rating">
          {!feedback ? 'Not rated' : feedback.rating === 'good' ? '👍 Good' : '👎 Bad'}
          {feedback?.reason && <span className="block text-stone-600">{feedback.reason}</span>}
          {feedback?.note && <span className="block text-stone-500">{feedback.note}</span>}
        </Field>
        <Field label="Duration">{durationText(line.durationMs)}</Field>
        <div className="col-span-2 min-w-0">
          <dt className="text-[11px] text-stone-500">Run id</dt>
          <dd className="text-[12.5px] text-stone-900"><CopyRunId runId={line.runId} /></dd>
        </div>
      </dl>

      {loading && <div className="mt-4"><RubyLoader variant="section" /></div>}
      {error && <div className="mt-4"><ErrorBanner title="Failed to load this run" detail={error} /></div>}

      {run && !loading && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-5">
          <div className="space-y-4 min-w-0">
            <section>
              <SectionTitle>Vendor tokens spent → Ruby Tokens charged</SectionTitle>
              <ul className="space-y-0.5 font-mono text-[12px] text-stone-800">
                {math.map((m, i) => (
                  <li key={i} className={i === math.length - 1 ? 'font-semibold text-stone-900 pt-1 mt-1 border-t border-stone-200' : 'text-stone-800'}>{m}</li>
                ))}
              </ul>
              {detail!.spend.length > 0 && (
                <p className="text-[11px] text-stone-500 mt-1.5">{detail!.spend.length} spend record{detail!.spend.length === 1 ? '' : 's'} on this run.</p>
              )}
            </section>

            {run.attempts?.length > 0 && (
              <section>
                <SectionTitle>Attempts</SectionTitle>
                <ul className="divide-y divide-stone-100">
                  {run.attempts.map((a, i) => (
                    <li key={i} className="py-1 flex items-start justify-between gap-3 text-[12px]">
                      <span className="font-mono text-stone-800 min-w-0 break-words">{attemptLine(a)}</span>
                      <span className={`shrink-0 ${a.ok ? 'text-emerald-700' : 'text-rose-600'}`}>
                        {a.ok ? 'ok' : 'failed'}{a.latencyMs ? <span className="text-stone-500"> · {durationText(a.latencyMs)}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {run.promptParts && Object.keys(run.promptParts).length > 0 && (
              <section>
                <SectionTitle>Prompt layers</SectionTitle>
                <div className="divide-y divide-stone-100">
                  {Object.entries(run.promptParts).map(([layer, text]) => (
                    <div key={layer} className="py-1.5">
                      <span className="font-mono text-[11px] text-stone-500">{layer}</span>
                      <p className="text-[12.5px] text-stone-800 whitespace-pre-wrap break-words">{text}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {run.promptFinal && (
              <section>
                <SectionTitle>Final prompt</SectionTitle>
                <p className="text-[12.5px] text-stone-800 whitespace-pre-wrap break-words">{run.promptFinal}</p>
              </section>
            )}

            {run.error && (
              <section>
                <SectionTitle>Error</SectionTitle>
                <p className="text-[12.5px] text-rose-700">{run.error.userMessage || run.error.message}</p>
                <p className="font-mono text-[11px] text-stone-500">{run.error.step} · {run.error.code}</p>
              </section>
            )}
          </div>

          <div className="max-w-[200px]">
            <SectionTitle>Output</SectionTitle>
            {run.output ? (
              <ShimmerImage
                src={run.output.url}
                alt={`Output for run ${run.runId}`}
                aspectRatio="1/1"
                className="rounded-lg border border-stone-200"
                objectFit="contain"
              />
            ) : (
              <div className="aspect-square rounded-lg border border-dashed border-stone-200 flex items-center justify-center text-[12px] text-stone-400">
                No output
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerRunDetails;
